import { useTranslations } from 'next-intl';

import { FormattedMessage } from '@flipster/i18n';
import { css } from '@flipster/styled-system/css';
import { center, circle, stack } from '@flipster/styled-system/patterns';
import { ProdStaticPath } from '@flipster/utils/url';

const Explain = () => {
  const t = useTranslations();
  return (
    <section
      className={stack({
        direction: { base: 'column', smallDesktop: 'row' },
        color: 'content.default.level-5',
        gap: { base: 48, smallDesktop: 20 },
        mt: 32,
        mb: 48,
      })}
    >
      <div className={explainItemStyle}>
        <h2>
          <FormattedMessage id='Autogen_common.r251126.s_Benefits_of_zero_spread' />
        </h2>
        <div
          className={css({
            display: { base: 'none', smallDesktop: 'block' },
            bgColor: 'background.default.level-3',
            rounded: 16,
            px: 16,
            py: 24,
            my: 16,
            textAlign: 'center',
          })}
        >
          <img
            src={`${ProdStaticPath}/images/web/zero-spread-card.webp`}
            alt='zero-spread-card'
            width={100}
            height={54}
            fetchPriority='high'
          />
        </div>
        <p>
          <FormattedMessage id='Autogen_common.r251126.s_Zero_spread_means_you_trade_at_the_price_you_see_because_every_trade_executes_at_the_exact_market_price_No_surprises' />
        </p>
      </div>
      <div className={explainItemStyle}>
        <h2>
          <FormattedMessage id='Autogen_common.r251126.s_What_is_spread' />
        </h2>
        <BidAndAsk
          bid='10,000'
          ask='10,200'
          description={t('Autogen_common.r251126.s_Price_difference_causes_200_spread')}
        />
        <p>
          <FormattedMessage id='Autogen_common.r251126.s_Spread_is_the_gap_between_buy_and_sell_prices_On_most_platforms_this_gap_can_make_your_execution_price_differ_from_what_you_see' />
        </p>
      </div>
      <div className={explainItemStyle}>
        <h2>
          <FormattedMessage id='Autogen_common.r251126.s_What_is_zero_spread' />
        </h2>
        <BidAndAsk
          bid='10,000'
          ask='10,000'
          description={t('Autogen_common.r251126.s_No_price_difference_and_executed_at_the_price_you_see')}
        />
        <p>
          <FormattedMessage id='Autogen_common.r251126.s_With_zero_spread_there_is_no_price_difference_So_the_price_you_see_is_always_the_price_you_get' />
        </p>
      </div>
    </section>
  );
};

export default Explain;

const explainItemStyle = css({
  flex: 1,
  bgColor: { base: 'transparent', smallDesktop: 'background.default.level-1' },
  rounded: { smallDesktop: 16 },
  py: { smallDesktop: 24 },
  px: { smallDesktop: 16 },
  '& > h2': { textStyle: 'Title/M', mb: 8 },
  '& > p': { textStyle: 'Text/M/Regular', color: 'content.default.level-3' },
});

const BidAndAsk = ({ bid, ask, description }: { bid: string; ask: string; description: string }) => {
  const isEqual = bid === ask;

  return (
    <div
      className={css({
        bgColor: { base: 'background.default.level-1', smallDesktop: 'background.default.level-3' },
        rounded: { base: 8, smallDesktop: 16 },
        textAlign: 'center',
        px: 16,
        py: 12,
        my: 16,
      })}
    >
      <div
        className={center({
          gap: 32,
          pb: 12,
          mb: 12,
          borderBottomWidth: '1px',
          borderBottomStyle: 'solid',
          borderColor: { base: 'border.default.level-1', smallDesktop: 'border.default.level-2' },
        })}
      >
        <div>
          <p className={css({ textStyle: 'Text/S/Strong' })}>
            <FormattedMessage id='Autogen_common.r251126.s_Bid' />
          </p>
          <p className={css({ textStyle: 'Text/L/Strong', color: 'content.accent.buy' })}>{bid}</p>
        </div>
        <div
          className={circle({
            color: 'inverse.white',
            bgColor: 'background.accent.secondary.tint',
            pb: 6,
            w: 39,
            h: 39,
          })}
        >
          <p className={css({ fontSize: 40 })}>{isEqual ? '=' : '≠'}</p>
        </div>
        <div>
          <p className={css({ textStyle: 'Text/S/Strong' })}>
            <FormattedMessage id='Autogen_common.r251126.s_Ask' />
          </p>
          <p className={css({ textStyle: 'Text/L/Strong', color: 'content.accent.sell' })}>{ask}</p>
        </div>
      </div>
      <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-3' })}>{description}</p>
    </div>
  );
};
